/**
 * assets/adv-mega-menu.js
 * ADV TRIBE Marketplace v6 — Mega Menu Interaction Controller
 * Store: advtribes.myshopify.com
 *
 * Responsibilities:
 *   - Desktop: hover/focus mega panel open/close with keyboard support
 *   - Mobile: hamburger drawer open/close with submenu accordions
 *   - Accessibility: ARIA updates, focus trapping, ESC key, keyboard nav
 *   - Touch: tap-to-open on desktop (prevents hover conflicts on iPad)
 *   - Sticky header: scroll-triggered class
 *
 * Architecture:
 *   - Single IIFE to avoid global scope pollution
 *   - All selectors use data-adv-* attributes (never class selectors)
 *     to avoid conflicts with Minimog's existing class-based JS
 *   - Event delegation where possible
 *   - No dependencies (vanilla JS only)
 *
 * Browser support: Modern evergreen + iOS Safari 14+
 */

(function () {
  'use strict';

  // =========================================================================
  // CONSTANTS
  // =========================================================================

  /** Duration (ms) to wait before closing panel after mouse leaves nav item.
   *  Gives user time to move cursor into the open panel. */
  var CLOSE_DELAY_MS = 200;

  /** Minimum scroll distance (px) before sticky class is applied. */
  var STICKY_THRESHOLD_PX = 4;

  // =========================================================================
  // STATE
  // =========================================================================

  /** Currently open desktop panel key, or null. @type {string|null} */
  var activePanel = null;

  /** setTimeout handle for delayed panel close. @type {number|null} */
  var closeTimer = null;

  /** Whether any mega panel is in an exit animation. @type {boolean} */
  var isAnimating = false;

  // =========================================================================
  // ELEMENT REFERENCES
  // =========================================================================

  var header       = document.querySelector('[data-adv-header]');
  var headerSentinel = document.querySelector('[data-adv-header-sentinel]');
  var headerSpacer   = document.querySelector('[data-adv-header-spacer]');
  var headerPinObserver = null;
  var hamburger    = document.querySelector('[data-adv-hamburger]');
  var mobileDrawer = document.querySelector('[data-adv-mobile-drawer]');
  var mobileBackdrop = document.querySelector('[data-adv-mobile-backdrop]');
  var overlay      = document.querySelector('[data-adv-overlay]');
  var navItems     = document.querySelectorAll('[data-adv-nav-item]');
  var triggers     = document.querySelectorAll('[data-adv-trigger]');
  var panelCloseButtons = document.querySelectorAll('[data-adv-panel-close]');
  var mobileCloseBtn    = document.querySelector('[data-adv-mobile-close]');
  var mobileExpandButtons = document.querySelectorAll('[data-adv-mobile-expand]');
  var lastSearchTrigger = null;
  var searchCloseTimer = null;
  var predictiveAbortController = null;
  var predictiveDebounceTimer = null;

  /** Saved html/body inline styles + scroll position while search scroll-lock is active, or null when unlocked. */
  var scrollLockState = null;

  /**
   * Scroll position captured at pointerdown/touchstart on the search trigger,
   * before the click fires. Consumed (and cleared) by the next lockPageScroll()
   * call. Capturing this early — rather than inside the click handler — means
   * any scroll-into-view side effect a click driver performs ahead of
   * dispatching mouse events cannot corrupt the position we intend to restore.
   */
  var capturedTriggerScroll = null;

  // Guard: if header element not found, bail out gracefully
  if (!header) { return; }

  // =========================================================================
  // UTILITY FUNCTIONS
  // =========================================================================

  /**
   * Returns all focusable elements within a given container.
   * @param {Element} container - The DOM element to search within.
   * @returns {NodeList} Focusable elements.
   */
  function getFocusableElements(container) {
    return container.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), ' +
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
    );
  }

  /**
   * Returns the panel element for a given key.
   * @param {string} key - Panel key (e.g. "activity", "brands").
   * @returns {Element|null}
   */
  function getPanelByKey(key) {
    return document.querySelector('[data-adv-panel="' + key + '"]');
  }

  /**
   * Returns the trigger button element for a given key.
   * @param {string} key - Panel key.
   * @returns {Element|null}
   */
  function getTriggerByKey(key) {
    return document.querySelector('[data-adv-trigger="' + key + '"]');
  }

  /**
   * Returns the active search popup element.
   * @returns {Element|null}
   */
  function getSearchPopup() {
    return document.querySelector('[data-search-popup]');
  }

  /**
   * Returns the active search popup container element.
   * @returns {Element|null}
   */
  function getSearchContainer() {
    var popup = getSearchPopup();
    return popup ? popup.querySelector('[data-search-container]') : null;
  }

  /**
   * Returns the active search input element.
   * @returns {HTMLInputElement|null}
   */
  function getSearchInput() {
    var popup = getSearchPopup();
    return popup ? popup.querySelector('[data-search-input]') : null;
  }

  /**
   * Returns the active search form element.
   * @returns {HTMLFormElement|null}
   */
  function getSearchForm() {
    var popup = getSearchPopup();
    return popup ? popup.querySelector('form.m-search-form') : null;
  }

  /**
   * Returns true when the theme search popup is visibly open.
   * @returns {boolean}
   */
  function isSearchPopupOpen() {
    var popup = getSearchPopup();
    return Boolean(popup && popup.classList.contains('opacity-100') && !popup.classList.contains('invisible'));
  }

  /**
   * Updates aria-expanded on every search trigger button.
   * @param {boolean} expanded
   */
  function setSearchTriggerExpanded(expanded) {
    document.querySelectorAll('[data-open-search-popup] button').forEach(function (button) {
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /**
   * Locks document scroll for the search overlay: freezes html/body against
   * wheel, touch and keyboard scrolling while preserving the current scroll
   * position for restoration on unlock. Idempotent — a second call while
   * already locked is a no-op, guarding against duplicate open handlers.
   */
  function lockPageScroll() {
    if (scrollLockState) { return; }

    var htmlEl = document.documentElement;
    var bodyEl = document.body;

    // Prefer the position captured at pointerdown/touchstart (before any
    // click-time side effects); fall back to reading it live for opens that
    // didn't go through a pointer gesture (e.g. programmatic/keyboard).
    var scrollX = capturedTriggerScroll ? capturedTriggerScroll.x : (window.scrollX || 0);
    var scrollY = capturedTriggerScroll ? capturedTriggerScroll.y : (window.scrollY || window.pageYOffset || htmlEl.scrollTop || bodyEl.scrollTop || 0);
    capturedTriggerScroll = null;

    var scrollbarWidth = window.innerWidth - htmlEl.clientWidth;

    scrollLockState = {
      scrollX: scrollX,
      scrollY: scrollY,
      htmlOverflow: htmlEl.style.overflow,
      htmlOverflowY: htmlEl.style.overflowY,
      bodyOverflow: bodyEl.style.overflow,
      bodyPosition: bodyEl.style.position,
      bodyTop: bodyEl.style.top,
      bodyLeft: bodyEl.style.left,
      bodyRight: bodyEl.style.right,
      bodyWidth: bodyEl.style.width,
      bodyPaddingRight: bodyEl.style.paddingRight
    };

    htmlEl.style.overflow = 'hidden';
    htmlEl.style.overflowY = 'hidden';

    bodyEl.style.position = 'fixed';
    bodyEl.style.top = (-scrollY) + 'px';
    bodyEl.style.left = (-scrollX) + 'px';
    bodyEl.style.right = '0';
    bodyEl.style.width = '100%';
    bodyEl.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      var currentPaddingRight = parseFloat(window.getComputedStyle(bodyEl).paddingRight) || 0;
      bodyEl.style.paddingRight = (currentPaddingRight + scrollbarWidth) + 'px';
    }
  }

  /**
   * Reverses lockPageScroll(): restores prior html/body inline styles and
   * the exact scroll position that was active before the lock. Idempotent —
   * a second call while already unlocked is a no-op.
   */
  function unlockPageScroll() {
    if (!scrollLockState) { return; }

    var htmlEl = document.documentElement;
    var bodyEl = document.body;
    var state = scrollLockState;
    scrollLockState = null;

    htmlEl.style.overflow = state.htmlOverflow;
    htmlEl.style.overflowY = state.htmlOverflowY;

    bodyEl.style.position = state.bodyPosition;
    bodyEl.style.top = state.bodyTop;
    bodyEl.style.left = state.bodyLeft;
    bodyEl.style.right = state.bodyRight;
    bodyEl.style.width = state.bodyWidth;
    bodyEl.style.overflow = state.bodyOverflow;
    bodyEl.style.paddingRight = state.bodyPaddingRight;

    // Deferred one frame: restoring scroll position in the same tick that
    // clears `position: fixed` is unreliable because the browser has not
    // yet reflowed html/body into their restored (scrollable) state.
    window.requestAnimationFrame(function () {
      window.scrollTo(state.scrollX, state.scrollY);
    });
  }

  /**
   * Clears transient page-level state applied by the search popup.
   */
  function clearSearchPageState() {
    unlockPageScroll();
    document.body.classList.remove('modal-open', 'search-open', 'overflow-hidden');
    document.documentElement.classList.remove('modal-open', 'search-open', 'overflow-hidden');
  }

  /**
   * Restores focus to the trigger used to open search.
   */
  function restoreSearchTriggerFocus() {
    if (lastSearchTrigger && typeof lastSearchTrigger.focus === 'function') {
      lastSearchTrigger.focus();
    }
  }

  /**
   * Opens the existing Minimog search popup with ADV TRIBE state handling.
   * @param {Element} trigger
   */
  function openSearchPopup(trigger) {
    var popup = getSearchPopup();
    var container = getSearchContainer();
    var input = getSearchInput();
    if (!popup || !container) { return; }

    if (searchCloseTimer) {
      window.clearTimeout(searchCloseTimer);
      searchCloseTimer = null;
    }

    var triggerButton = trigger && trigger.matches('button') ? trigger : trigger && trigger.querySelector('button');
    lastSearchTrigger = triggerButton || trigger || lastSearchTrigger;
    setSearchTriggerExpanded(true);
    closeActivePanel();
    if (mobileDrawer && mobileDrawer.classList.contains('adv-mm__mobile-drawer--open')) {
      closeMobileDrawer();
    }

    popup.classList.remove('invisible');
    popup.classList.add('opacity-100');
    popup.setAttribute('aria-hidden', 'false');
    container.classList.add('md:translate-y-0');
    lockPageScroll();

    window.setTimeout(function () {
      container.classList.remove('duration-300');
      if (input && typeof input.focus === 'function') {
        input.focus();
      }
    }, 50);
  }

  /**
   * Closes the search popup and performs all cleanup through one path.
   * @param {boolean} returnFocus
   */
  function closeSearchPopup(returnFocus) {
    var popup = getSearchPopup();
    var container = getSearchContainer();
    if (!popup) { return; }

    if (searchCloseTimer) {
      window.clearTimeout(searchCloseTimer);
    }

    if (container) {
      container.classList.remove('md:translate-y-0');
      container.classList.add('duration-300');
    }
    popup.classList.remove('opacity-100');
    popup.setAttribute('aria-hidden', 'true');
    setSearchTriggerExpanded(false);
    clearSearchPageState();

    if (returnFocus) {
      restoreSearchTriggerFocus();
    }

    searchCloseTimer = window.setTimeout(function () {
      popup.classList.add('invisible');
      searchCloseTimer = null;
    }, 350);
  }

  /**
   * Builds the standard Shopify search URL from the popup form.
   * @param {HTMLFormElement} form
   * @returns {string}
   */
  function buildSearchUrl(form) {
    var action = form.getAttribute('action') || '/search';
    var params = new URLSearchParams(new FormData(form));
    return action + '?' + params.toString();
  }

  /**
   * Submits search without relying on Minimog's native o.submit() click helper.
   * @param {Event} e
   * @param {HTMLFormElement} form
   */
  function submitSearchForm(e, form) {
    var input = form.querySelector('[name="q"]');
    if (!input || !input.value.trim()) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    window.location.href = buildSearchUrl(form);
  }

  /**
   * Toggles predictive-search loading and submit controls.
   * @param {boolean} loading
   */
  function setPredictiveLoading(loading) {
    var popup = getSearchPopup();
    if (!popup) { return; }
    var spinner = popup.querySelector('[data-spinner]');
    var submit = popup.querySelector('[data-search-submit]');
    if (spinner) {
      spinner.style.visibility = loading ? 'visible' : 'hidden';
    }
    if (submit) {
      submit.style.visibility = loading ? 'hidden' : 'visible';
    }
  }

  /**
   * Shows or hides the clear-search control.
   * @param {boolean} visible
   */
  function setClearSearchVisible(visible) {
    var popup = getSearchPopup();
    var clear = popup ? popup.querySelector('[data-clear-search]') : null;
    if (clear) {
      clear.style.visibility = visible ? 'visible' : 'hidden';
    }
  }

  /**
   * Updates the predictive-search status message.
   * @param {string} query
   * @param {boolean} hasResults
   */
  function updatePredictiveMessage(query, hasResults) {
    var popup = getSearchPopup();
    if (!popup) { return; }
    var count = popup.querySelector('[data-search-count]');
    var message = popup.querySelector('[data-message]');
    var queryNode = popup.querySelector('[data-query]');
    if (!count || !message || !queryNode) { return; }

    queryNode.textContent = query;
    message.textContent = hasResults ? message.dataset.resultsTitle : message.dataset.noResults;
    count.classList.remove('hidden');
  }

  /**
   * Resets the predictive result view to the popular-searches state.
   */
  function closePredictiveResults() {
    var popup = getSearchPopup();
    if (!popup) { return; }
    var results = popup.querySelector('[data-predictive-search]');
    var popular = popup.querySelector('[data-popular-searches]');
    var count = popup.querySelector('[data-search-count]');
    var predictiveRoot = popup.querySelector('predictive-search');
    if (results) {
      results.innerHTML = '';
      results.classList.add('hidden');
    }
    if (popular) {
      popular.classList.remove('hidden');
    }
    if (count) {
      count.classList.add('hidden');
    }
    if (predictiveRoot) {
      predictiveRoot.removeAttribute('open');
      predictiveRoot.removeAttribute('results');
    }
    setPredictiveLoading(false);
    setClearSearchVisible(false);
  }

  /**
   * Renders Shopify predictive-search section HTML into the existing contract.
   * @param {string} query
   * @param {string} markup
   */
  function renderPredictiveResults(query, markup) {
    var popup = getSearchPopup();
    if (!popup) { return; }
    var results = popup.querySelector('[data-predictive-search]');
    var popular = popup.querySelector('[data-popular-searches]');
    var predictiveRoot = popup.querySelector('predictive-search');
    if (!results) { return; }

    results.innerHTML = markup || '<div data-search-items-wrapper></div>';
    var itemsWrapper = results.querySelector('[data-search-items-wrapper]');
    var hasResults = Boolean(itemsWrapper && itemsWrapper.childElementCount > 0);
    results.classList.remove('hidden');
    if (popular) {
      popular.classList.add('hidden');
    }
    if (predictiveRoot) {
      predictiveRoot.setAttribute('open', 'true');
      if (hasResults) {
        predictiveRoot.setAttribute('results', 'true');
      } else {
        predictiveRoot.removeAttribute('results');
      }
    }
    updatePredictiveMessage(query, hasResults);
    setPredictiveLoading(false);
    setClearSearchVisible(true);
  }

  /**
   * Fetches predictive-search HTML through Shopify's native endpoint.
   * @param {string} query
   */
  function fetchPredictiveResults(query) {
    var popup = getSearchPopup();
    var root = popup ? popup.querySelector('predictive-search') : null;
    var endpoint = window.MinimogSettings && window.MinimogSettings.routes
      ? window.MinimogSettings.routes.predictive_search_url
      : '/search/suggest';
    if (!popup || !root || !endpoint) { return; }

    if (predictiveAbortController) {
      predictiveAbortController.abort();
    }
    predictiveAbortController = new AbortController();

    var searchFields = 'title,product_type,vendor,variants.sku,variants.title';
    if (root.dataset.searchByTag === 'true') { searchFields += ',tag'; }
    if (root.dataset.searchByBody === 'true') { searchFields += ',body'; }

    var params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[options][unavailable_products]', root.dataset.unavailableProductsOption || 'last');
    params.set('resources[options][fields]', searchFields);
    params.set('section_id', 'predictive-search');

    setPredictiveLoading(true);
    setClearSearchVisible(false);

    fetch(endpoint + '?' + params.toString(), { signal: predictiveAbortController.signal })
      .then(function (response) {
        if (!response.ok) { throw new Error(String(response.status)); }
        return response.text();
      })
      .then(function (text) {
        var section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        renderPredictiveResults(query, section ? section.innerHTML : '');
      })
      .catch(function (error) {
        if (error.name === 'AbortError') { return; }
        setPredictiveLoading(false);
        setClearSearchVisible(true);
      });
  }

  /**
   * Handles input changes for the predictive-search repair path.
   */
  function handlePredictiveInput() {
    var input = getSearchInput();
    if (!input) { return; }
    var query = input.value.trim();
    window.clearTimeout(predictiveDebounceTimer);
    predictiveDebounceTimer = window.setTimeout(function () {
      if (!query) {
        closePredictiveResults();
        return;
      }
      fetchPredictiveResults(query);
    }, 350);
  }

  /**
   * Traps keyboard focus within the search popup when it is open.
   * @param {KeyboardEvent} e
   */
  function trapSearchFocus(e) {
    if (!isSearchPopupOpen() || e.key !== 'Tab') { return; }
    var popup = getSearchPopup();
    if (!popup) { return; }
    var focusables = getFocusableElements(popup);
    if (focusables.length === 0) { return; }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * Cached media query state for reduced-motion preference.
   * Updated via listener to avoid repeated matchMedia evaluations on hot paths.
   * @type {boolean}
   */
  var reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var isReducedMotion = reducedMotionMQ.matches;
  reducedMotionMQ.addEventListener('change', function (e) {
    isReducedMotion = e.matches;
  });

  /**
   * Checks if user prefers reduced motion.
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return isReducedMotion;
  }

  /**
   * Cached media query state for desktop breakpoint (>=1200px).
   * Updated via listener to avoid repeated matchMedia evaluations on hot paths.
   * @type {boolean}
   */
  var desktopMQ = window.matchMedia('(min-width: 1200px)');
  var isDesktopView = desktopMQ.matches;
  desktopMQ.addEventListener('change', function (e) {
    isDesktopView = e.matches;
  });

  /**
   * Checks if the current viewport is desktop width (>=1200px).
   * @returns {boolean}
   */
  function isDesktop() {
    return isDesktopView;
  }

  // =========================================================================
  // OVERLAY CONTROL
  // =========================================================================

  /**
   * Shows the desktop overlay backdrop.
   */
  function showOverlay() {
    if (!overlay) { return; }
    overlay.classList.add('adv-mm__overlay--visible');
    // Trigger reflow so transition fires
    overlay.getBoundingClientRect();
    overlay.classList.add('adv-mm__overlay--active');
  }

  /**
   * Hides the desktop overlay backdrop.
   */
  function hideOverlay() {
    if (!overlay) { return; }
    overlay.classList.remove('adv-mm__overlay--active');
    var el = overlay;
    el.addEventListener('transitionend', function onEnd() {
      el.classList.remove('adv-mm__overlay--visible');
      el.removeEventListener('transitionend', onEnd);
    }, { once: true });
  }

  // =========================================================================
  // DESKTOP PANEL CONTROL
  // =========================================================================

  /**
   * Opens a mega panel by key. Closes any currently open panel first.
   * Updates all ARIA attributes.
   * @param {string} key - Panel key (e.g. "activity", "brands").
   * @param {boolean} [focusFirst=false] - Whether to move focus into the panel.
   */
  function openPanel(key, focusFirst) {
    if (activePanel === key) { return; }

    // Always close previous panel immediately (no animation overlap when switching)
    if (activePanel) {
      closePanelImmediate(activePanel);
      hideOverlay();
    }

    var panel   = getPanelByKey(key);
    var trigger = getTriggerByKey(key);

    if (!panel) { return; }

    activePanel = key;

    // ARIA
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Remove hidden, add entering animation class
    panel.removeAttribute('hidden');
    if (!prefersReducedMotion()) {
      panel.classList.add('adv-mm__panel--entering');
      panel.addEventListener('animationend', function onEnterEnd() {
        panel.classList.remove('adv-mm__panel--entering');
        panel.removeEventListener('animationend', onEnterEnd);
        isAnimating = false;
      }, { once: true });
    }
    isAnimating = true;

    showOverlay();

    if (focusFirst) {
      var focusables = getFocusableElements(panel);
      if (focusables.length > 0) {
        setTimeout(function () { focusables[0].focus(); }, 50);
      }
    }
  }

  /**
   * Closes a panel with exit animation.
   * @param {string} key - Panel key.
   */
  function closePanel(key) {
    var panel   = getPanelByKey(key);
    var trigger = getTriggerByKey(key);

    if (!panel) { return; }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    if (prefersReducedMotion()) {
      panel.setAttribute('hidden', '');
      activePanel = null;
      hideOverlay();
      return;
    }

    panel.classList.add('adv-mm__panel--exiting');
    panel.addEventListener('animationend', function onExitEnd() {
      panel.classList.remove('adv-mm__panel--exiting');
      panel.setAttribute('hidden', '');
      panel.removeEventListener('animationend', onExitEnd);
      if (activePanel === key) {
        activePanel = null;
      }
      isAnimating = false;
    }, { once: true });

    hideOverlay();
  }

  /**
   * Closes a panel immediately (no animation). Used when switching panels.
   * @param {string} key - Panel key.
   */
  function closePanelImmediate(key) {
    var panel   = getPanelByKey(key);
    var trigger = getTriggerByKey(key);
    if (!panel) { return; }
    if (trigger) { trigger.setAttribute('aria-expanded', 'false'); }
    panel.classList.remove('adv-mm__panel--entering', 'adv-mm__panel--exiting');
    panel.setAttribute('hidden', '');
    if (activePanel === key) { activePanel = null; }
  }

  /**
   * Closes the active panel (if any) with animation.
   */
  function closeActivePanel() {
    if (activePanel) {
      closePanel(activePanel);
    }
  }

  /**
   * Schedules a panel close after CLOSE_DELAY_MS.
   * Call clearCloseTimer() if cursor re-enters before timeout fires.
   */
  function scheduleClose() {
    clearCloseTimer();
    closeTimer = setTimeout(closeActivePanel, CLOSE_DELAY_MS);
  }

  /**
   * Cancels any pending scheduled close.
   */
  function clearCloseTimer() {
    if (closeTimer !== null) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  // =========================================================================
  // MOBILE DRAWER CONTROL
  // =========================================================================

  /**
   * Opens the mobile navigation drawer.
   * Traps focus within the drawer and prevents body scroll.
   */
  function openMobileDrawer() {
    if (!mobileDrawer || !hamburger) { return; }

    mobileDrawer.removeAttribute('hidden');
    // Trigger layout then animate
    mobileDrawer.getBoundingClientRect();
    mobileDrawer.classList.add('adv-mm__mobile-drawer--open');

    hamburger.setAttribute('aria-expanded', 'true');

    document.body.style.overflow = 'hidden';

    if (mobileBackdrop) {
      mobileBackdrop.classList.add('adv-mm__mobile-backdrop--visible');
      mobileBackdrop.getBoundingClientRect();
      mobileBackdrop.classList.add('adv-mm__mobile-backdrop--active');
    }

    // Focus first focusable element inside drawer
    var focusables = getFocusableElements(mobileDrawer);
    if (focusables.length > 0) {
      setTimeout(function () { focusables[0].focus(); }, 60);
    }
  }

  /**
   * Closes the mobile navigation drawer.
   * Restores body scroll and focus to hamburger button.
   */
  function closeMobileDrawer() {
    if (!mobileDrawer || !hamburger) { return; }

    mobileDrawer.classList.remove('adv-mm__mobile-drawer--open');
    hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    if (mobileBackdrop) {
      mobileBackdrop.classList.remove('adv-mm__mobile-backdrop--active');
      var bd = mobileBackdrop;
      bd.addEventListener('transitionend', function onEnd() {
        bd.classList.remove('adv-mm__mobile-backdrop--visible');
        bd.removeEventListener('transitionend', onEnd);
      }, { once: true });
    }

    var drawer = mobileDrawer;
    if (prefersReducedMotion()) {
      drawer.setAttribute('hidden', '');
      hamburger.focus();
      return;
    }

    drawer.addEventListener('transitionend', function onDone() {
      drawer.setAttribute('hidden', '');
      drawer.removeEventListener('transitionend', onDone);
    }, { once: true });

    hamburger.focus();
  }

  /**
   * Toggles a mobile submenu accordion by ID.
   * @param {string} expandKey - The expand key matching data-adv-mobile-expand.
   * @param {Element} btn - The expand button element.
   */
  function toggleMobileSubmenu(expandKey, btn) {
    var submenu = document.getElementById('adv-mm-' + expandKey);
    if (!submenu) { return; }

    var isExpanded = btn.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      btn.setAttribute('aria-expanded', 'false');
      submenu.setAttribute('hidden', '');
    } else {
      btn.setAttribute('aria-expanded', 'true');
      submenu.removeAttribute('hidden');
    }
  }

  /**
   * Traps keyboard focus within the mobile drawer when it is open.
   * @param {KeyboardEvent} e
   */
  function trapDrawerFocus(e) {
    if (!mobileDrawer || !mobileDrawer.classList.contains('adv-mm__mobile-drawer--open')) {
      return;
    }
    var focusables = getFocusableElements(mobileDrawer);
    if (focusables.length === 0) { return; }
    var first = focusables[0];
    var last  = focusables[focusables.length - 1];

    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift+Tab: if on first element, wrap to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: if on last element, wrap to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  }

  // =========================================================================
  // KEYBOARD NAVIGATION — DESKTOP
  // =========================================================================

  /**
   * Returns an ordered array of trigger keys (for arrow key navigation).
   * @returns {string[]}
   */
  function getTriggerKeys() {
    var keys = [];
    triggers.forEach(function (t) {
      keys.push(t.getAttribute('data-adv-trigger'));
    });
    return keys;
  }

  /**
   * Handles ArrowLeft / ArrowRight navigation between trigger buttons.
   * @param {KeyboardEvent} e
   * @param {string} currentKey - Current trigger key.
   */
  function handleTriggerArrowKey(e, currentKey) {
    var keys = getTriggerKeys();
    var idx  = keys.indexOf(currentKey);
    if (idx === -1) { return; }

    var nextIdx;
    if (e.key === 'ArrowRight') {
      nextIdx = (idx + 1) % keys.length;
    } else if (e.key === 'ArrowLeft') {
      nextIdx = (idx - 1 + keys.length) % keys.length;
    } else {
      return;
    }

    e.preventDefault();
    var nextTrigger = getTriggerByKey(keys[nextIdx]);
    if (nextTrigger) {
      nextTrigger.focus();
      if (activePanel) {
        openPanel(keys[nextIdx], false);
      }
    }
  }

  // =========================================================================
  // STICKY HEADER
  // =========================================================================

  /**
   * Applies or removes the scrolled class on the header based on scroll position.
   */
  function updateStickyState() {
    if (!header) { return; }
    if (window.scrollY > STICKY_THRESHOLD_PX) {
      header.classList.add('adv-mm__header--scrolled');
    } else {
      header.classList.remove('adv-mm__header--scrolled');
    }
  }

  /**
   * Pins the header to the viewport (position:fixed) and grows the spacer
   * to the header's current measured height so removing it from flow does
   * not shift page content upward.
   *
   * Skipped while the search overlay's scroll lock is active: body is
   * already position:fixed at that point (page is visually frozen), and
   * changing the spacer height mid-lock races with the browser's default
   * scroll-anchoring, which can fight unlockPageScroll's own scroll
   * restoration on close. Whatever pin state was set before the lock
   * engaged simply holds until it is released.
   */
  function pinHeader() {
    if (!header || header.classList.contains('adv-mm__header--pinned')) { return; }
    if (scrollLockState) { return; }
    if (headerSpacer) {
      headerSpacer.style.height = header.getBoundingClientRect().height + 'px';
    }
    header.classList.add('adv-mm__header--pinned');
  }

  /**
   * Reverses pinHeader(): returns the header to normal flow and collapses
   * the spacer back to zero height. Same scroll-lock guard as pinHeader().
   */
  function unpinHeader() {
    if (!header || !header.classList.contains('adv-mm__header--pinned')) { return; }
    if (scrollLockState) { return; }
    header.classList.remove('adv-mm__header--pinned');
    if (headerSpacer) {
      headerSpacer.style.height = '0px';
    }
  }

  /**
   * Wires the IntersectionObserver-driven pin fallback. Native
   * position:sticky is inert on this page because html/body carry
   * non-visible overflow, which per spec makes that ancestor -- not the
   * viewport -- the sticky containing block; since that ancestor never
   * itself scrolls, sticky never engages. Watching a 1px sentinel placed
   * where the header naturally sits reproduces the same visual behavior:
   * pin once the sentinel scrolls above the viewport, release once it is
   * back in view. Only runs when the merchant has sticky_header enabled
   * (adv-mm__header--sticky present) and IntersectionObserver exists.
   */
  function initHeaderPin() {
    if (headerPinObserver) {
      headerPinObserver.disconnect();
      headerPinObserver = null;
    }

    if (!header || !headerSentinel) { return; }
    if (!header.classList.contains('adv-mm__header--sticky')) { return; }
    if (!('IntersectionObserver' in window)) { return; }

    headerPinObserver = new IntersectionObserver(function (entries) {
      var entry = entries[0];
      if (entry.isIntersecting) {
        unpinHeader();
      } else if (entry.boundingClientRect.top < 0) {
        pinHeader();
      }
    }, { threshold: 0 });

    headerPinObserver.observe(headerSentinel);
  }

  // =========================================================================
  // EVENT BINDING — DESKTOP TRIGGERS
  // =========================================================================

  triggers.forEach(function (trigger) {
    var key = trigger.getAttribute('data-adv-trigger');

    // Mouse enter on trigger: open panel immediately.
    // No mouseleave here — closing is handled by the single header mouseleave below.
    trigger.addEventListener('mouseenter', function () {
      if (!isDesktop()) { return; }
      clearCloseTimer();
      openPanel(key, false);
    });

    // Click on trigger: toggle panel (supports keyboard+touch)
    trigger.addEventListener('click', function (e) {
      if (!isDesktop()) { return; }
      e.stopPropagation();
      clearCloseTimer();
      if (activePanel === key) {
        closePanel(key);
      } else {
        openPanel(key, false);
      }
    });

    // Keyboard: Enter or Space opens with focus-into-panel
    trigger.addEventListener('keydown', function (e) {
      if (!isDesktop()) { return; }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (activePanel === key) {
          closePanel(key);
          trigger.focus();
        } else {
          openPanel(key, true);
        }
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        openPanel(key, true);
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeActivePanel();
        trigger.focus();
      }
      handleTriggerArrowKey(e, key);
    });
  });

  // =========================================================================
  // EVENT BINDING — NAV ITEMS (mouse enter/leave for panel stay-open)
  // =========================================================================

  navItems.forEach(function (item) {
    item.addEventListener('mouseenter', function () {
      if (!isDesktop()) { return; }
      clearCloseTimer();
    });
    // mouseleave removed: closing handled by single header mouseleave
  });

  // =========================================================================
  // EVENT BINDING — PANEL CLOSE BUTTONS
  // =========================================================================

  panelCloseButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var key = btn.closest('[data-adv-panel]').getAttribute('data-adv-panel');
      var trigger = getTriggerByKey(key);
      closePanel(key);
      if (trigger) { trigger.focus(); }
    });
  });

  // =========================================================================
  // EVENT BINDING — HEADER MOUSELEAVE (single close trigger, no flicker)
  // All previous per-element mouseleave listeners are replaced by this one.
  // The panel stays open as long as cursor is anywhere inside the header
  // (which contains both the nav triggers and the panels wrapper).
  // Only when cursor leaves the entire header does the close timer start.
  // =========================================================================

  if (header) {
    header.addEventListener('mouseleave', function () {
      if (!isDesktop()) { return; }
      scheduleClose();
    });
    header.addEventListener('mouseenter', function () {
      if (!isDesktop()) { return; }
      clearCloseTimer();
    });
  }

  document.querySelectorAll('[data-adv-panel]').forEach(function (panel) {
    panel.addEventListener('mouseenter', function () {
      if (!isDesktop()) { return; }
      clearCloseTimer();
    });
  });

  // =========================================================================
  // EVENT BINDING — OVERLAY CLICK
  // Overlay is pointer-events:none so mouse events pass through it.
  // Outside-click close is handled by the document click listener below.
  // =========================================================================

  // =========================================================================
  // EVENT BINDING — HAMBURGER
  // =========================================================================

  if (hamburger) {
    hamburger.addEventListener('click', function () {
      if (mobileDrawer && mobileDrawer.classList.contains('adv-mm__mobile-drawer--open')) {
        closeMobileDrawer();
      } else {
        openMobileDrawer();
      }
    });
  }

  // =========================================================================
  // EVENT BINDING — MOBILE CLOSE BUTTON
  // =========================================================================

  if (mobileCloseBtn) {
    mobileCloseBtn.addEventListener('click', function () {
      closeMobileDrawer();
    });
  }

  // =========================================================================
  // EVENT BINDING — MOBILE BACKDROP CLICK
  // =========================================================================

  if (mobileBackdrop) {
    mobileBackdrop.addEventListener('click', function () {
      closeMobileDrawer();
    });
  }

  // =========================================================================
  // EVENT BINDING — MOBILE EXPAND BUTTONS
  // =========================================================================

  mobileExpandButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      var key = btn.getAttribute('data-adv-mobile-expand');
      toggleMobileSubmenu(key, btn);
    });
  });

  // =========================================================================
  // EVENT BINDING — GLOBAL KEYBOARD
  // =========================================================================

  document.addEventListener('keydown', function (e) {
    if (isSearchPopupOpen()) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeSearchPopup(true);
        return;
      }
      trapSearchFocus(e);
      if (e.defaultPrevented) { return; }
    }

    // ESC: close active desktop panel or mobile drawer
    if (e.key === 'Escape') {
      if (activePanel && isDesktop()) {
        var key = activePanel;
        var trigger = getTriggerByKey(key);
        closeActivePanel();
        if (trigger) { trigger.focus(); }
      }
      if (mobileDrawer && mobileDrawer.classList.contains('adv-mm__mobile-drawer--open')) {
        closeMobileDrawer();
      }
    }

    // Trap focus inside mobile drawer
    trapDrawerFocus(e);
  }, true);

  // =========================================================================
  // EVENT BINDING — SEARCH TRIGGER SCROLL CAPTURE (earliest possible point)
  // =========================================================================

  /**
   * Captures the page scroll position the moment a pointer/touch gesture
   * begins on the search trigger — before the click (and openSearchPopup's
   * lockPageScroll call) fires. Capture phase on document so it always runs
   * ahead of any other pointerdown/touchstart handler on the trigger itself.
   */
  function handleSearchTriggerPointerStart(e) {
    if (scrollLockState) { return; }
    if (!e.target.closest('[data-open-search-popup]')) { return; }
    capturedTriggerScroll = { x: window.scrollX || 0, y: window.scrollY || 0 };
  }
  document.addEventListener('pointerdown', handleSearchTriggerPointerStart, true);
  document.addEventListener('touchstart', handleSearchTriggerPointerStart, { capture: true, passive: true });

  // =========================================================================
  // EVENT BINDING — GLOBAL CLICK (close panel on outside click)
  // =========================================================================

  document.addEventListener('click', function (e) {
    var searchPopup = getSearchPopup();
    var searchTrigger = e.target.closest('[data-open-search-popup]');
    var searchForm = e.target.closest('form.m-search-form');

    if (searchTrigger) {
      e.preventDefault();
      e.stopImmediatePropagation();
      openSearchPopup(searchTrigger);
      return;
    }

    if (searchPopup && e.target.closest('[data-clear-search]')) {
      var input = getSearchInput();
      e.preventDefault();
      e.stopImmediatePropagation();
      if (input) {
        input.value = '';
        input.focus();
      }
      closePredictiveResults();
      return;
    }

    if (searchForm && e.target.closest('[data-search-submit]')) {
      e.stopImmediatePropagation();
      submitSearchForm(e, searchForm);
      return;
    }

    if (searchPopup && e.target.closest('[data-close-search]')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      closeSearchPopup(true);
      return;
    }

    if (searchPopup && e.target === searchPopup) {
      e.preventDefault();
      e.stopImmediatePropagation();
      closeSearchPopup(true);
      return;
    }

    if (!activePanel) { return; }
    // If click is inside the header (which contains both nav and panels), ignore
    if (header && header.contains(e.target)) { return; }
    closeActivePanel();
  }, true);

  var searchForm = getSearchForm();
  if (searchForm) {
    searchForm.addEventListener('submit', function (e) {
      var input = searchForm.querySelector('[name="q"]');
      if (!input || !input.value.trim()) {
        e.preventDefault();
        return;
      }
      e.stopImmediatePropagation();
    }, true);
  }

  var searchInput = getSearchInput();
  if (searchInput) {
    searchInput.addEventListener('input', handlePredictiveInput);
  }

  // =========================================================================
  // EVENT BINDING — SCROLL (sticky header)
  // =========================================================================

  var scrollTicking = false;
  window.addEventListener('scroll', function () {
    if (!scrollTicking) {
      requestAnimationFrame(function () {
        updateStickyState();
        scrollTicking = false;
      });
      scrollTicking = true;
    }
  }, { passive: true });

  // =========================================================================
  // EVENT BINDING — RESIZE (close panels when switching to mobile)
  // =========================================================================

  var resizeTicking = false;
  window.addEventListener('resize', function () {
    if (!resizeTicking) {
      requestAnimationFrame(function () {
        if (!isDesktop()) {
          // Close all desktop panels silently when resizing to mobile
          if (activePanel) {
            closePanelImmediate(activePanel);
            hideOverlay();
          }
        }
        // Header height can differ across breakpoints; re-measure the
        // spacer while pinned so a resize does not leave a stale gap.
        if (header && headerSpacer && header.classList.contains('adv-mm__header--pinned')) {
          headerSpacer.style.height = header.getBoundingClientRect().height + 'px';
        }
        resizeTicking = false;
      });
      resizeTicking = true;
    }
  }, { passive: true });

  // =========================================================================
  // INIT
  // =========================================================================

  /**
   * Initializes the mega menu controller.
   * Called once on DOMContentLoaded or immediately if DOM is already ready.
   */
  function init() {
    header = document.querySelector('[data-adv-header]');
    headerSentinel = document.querySelector('[data-adv-header-sentinel]');
    headerSpacer = document.querySelector('[data-adv-header-spacer]');

    // Set initial ARIA states
    triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });

    if (hamburger) {
      hamburger.setAttribute('aria-expanded', 'false');
    }

    setSearchTriggerExpanded(false);
    var initPopup = getSearchPopup();
    if (initPopup) {
      initPopup.setAttribute('aria-hidden', 'true');
    }

    // Initial sticky state check
    updateStickyState();
    initHeaderPin();

    // Ensure panels start hidden (guard against theme editor state bleed)
    document.querySelectorAll('[data-adv-panel]').forEach(function (panel) {
      if (!panel.hasAttribute('hidden')) {
        panel.setAttribute('hidden', '');
      }
    });

    // Expose public API on window for optional external integration
    window.advMegaMenu = {
      openPanel: openPanel,
      closePanel: closePanel,
      closeActivePanel: closeActivePanel,
      openMobileDrawer: openMobileDrawer,
      closeMobileDrawer: closeMobileDrawer
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  // =========================================================================
  // THEME EDITOR SUPPORT  (v7.0)
  //
  // shopify:section:load fires in the Theme Editor after a section (including
  // its blocks) is re-rendered. When blocks are added, removed, or reordered,
  // the cached NodeLists (triggers, panelCloseButtons, mobileExpandButtons)
  // become stale because the DOM elements they point to have been replaced.
  //
  // Strategy:
  //   1. Re-query all three NodeLists.
  //   2. Bind events only on elements that don't yet have the _advBound
  //      sentinel flag, preventing double-binding on elements that survived
  //      the re-render unchanged.
  //   3. Call init() to reset ARIA states and re-hide any open panels.
  //
  // shopify:section:unload fires just before the section is torn down.
  // We close any open panel and the mobile drawer to avoid orphaned state.
  //
  // PRODUCTION NOTE: These events only fire inside the Theme Editor iframe.
  // On the live storefront they are never dispatched, so this code path adds
  // zero overhead to production page loads.
  // =========================================================================

  document.addEventListener('shopify:section:load', function (e) {
    // Guard: only handle events from our header section
    if (!e.target || !e.target.querySelector('[data-adv-header]')) { return; }

    // Re-query stale NodeLists with fresh DOM references
    triggers            = document.querySelectorAll('[data-adv-trigger]');
    panelCloseButtons   = document.querySelectorAll('[data-adv-panel-close]');
    mobileExpandButtons = document.querySelectorAll('[data-adv-mobile-expand]');

    // Re-bind trigger events (mouseenter, click, keydown)
    triggers.forEach(function (trigger) {
      if (trigger._advBound) { return; }
      trigger._advBound = true;
      var key = trigger.getAttribute('data-adv-trigger');

      trigger.addEventListener('mouseenter', function () {
        if (!isDesktop()) { return; }
        clearCloseTimer();
        openPanel(key, false);
      });

      trigger.addEventListener('click', function (e2) {
        if (!isDesktop()) { return; }
        e2.stopPropagation();
        clearCloseTimer();
        if (activePanel === key) { closePanel(key); } else { openPanel(key, false); }
      });

      trigger.addEventListener('keydown', function (e2) {
        if (!isDesktop()) { return; }
        if (e2.key === 'Enter' || e2.key === ' ') {
          e2.preventDefault();
          if (activePanel === key) { closePanel(key); trigger.focus(); } else { openPanel(key, true); }
        }
        if (e2.key === 'ArrowDown') { e2.preventDefault(); openPanel(key, true); }
        if (e2.key === 'Escape') { e2.preventDefault(); closeActivePanel(); trigger.focus(); }
        handleTriggerArrowKey(e2, key);
      });
    });

    // Re-bind panel close buttons
    panelCloseButtons.forEach(function (btn) {
      if (btn._advBound) { return; }
      btn._advBound = true;
      btn.addEventListener('click', function () {
        var k = btn.closest('[data-adv-panel]').getAttribute('data-adv-panel');
        var t = getTriggerByKey(k);
        closePanel(k);
        if (t) { t.focus(); }
      });
    });

    // Re-bind mobile expand buttons
    mobileExpandButtons.forEach(function (btn) {
      if (btn._advBound) { return; }
      btn._advBound = true;
      btn.addEventListener('click', function () {
        var k = btn.getAttribute('data-adv-mobile-expand');
        toggleMobileSubmenu(k, btn);
      });
    });

    // Reset ARIA states and panel visibility
    init();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var isHeaderSection = Boolean(
      event &&
      event.target &&
      event.target.querySelector &&
      event.target.querySelector('[data-adv-header]')
    );

    // Clean up open state before section is removed from the DOM
    if (activePanel) {
      closePanelImmediate(activePanel);
      hideOverlay();
      activePanel = null;
    }
    closeMobileDrawer();

    if (isHeaderSection && headerPinObserver) {
      headerPinObserver.disconnect();
      headerPinObserver = null;
    }
  });

}());
